/* istanbul ignore file */

import {Heading, Radio, Skeleton, HStack, RadioGroup} from '@chakra-ui/react'
import React, {useMemo, useState} from 'react'
import Section from '../../components/section'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import {useParams} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useContentAsset} from '../../hooks/useScapiCustomApi'
import LenisProvider from '../../contexts/lenis'
import CmsModules from '../../leap/modules'
import {Chakra3Theme} from '../../leap/theme'
import {Box, Button} from '@chakra-ui/react3'
import {useContentDrawer} from '../../leap/components/content-drawer'

const LeapContent = () => {
    const {contentId} = useParams()
    const [theme, setTheme] = useState('dark')

    const cacheBreaker = useMemo(() => Date.now(), [])

    const {res} = useServerContext()
    if (res) {
        // no caching as this is for testing purposes only
        res.set('Cache-Control', `no-store, must-revalidate`)
        res.set('Pragma', `no-cache`)
        res.set('Expires', `0`)
    }

    useInitialDataLayer({pageType: 'CMS Content'})

    const {
        data: modules,
        isLoading,
        error
    } = useContentAsset({
        parameters: {
            assetId: contentId,
            // use to bypass sfcc caching of the content asset as this is a test page
            c_cache_breaker: cacheBreaker
        }
    })

    const contentDrawerCtrl = useContentDrawer()
    const openInSidebar = () => {
        contentDrawerCtrl.open({contentId, theme})
    }

    if (error) {
        console.error(`Error fetching leap content asset id ${contentId}`, error)
        const errorStatus = error?.response?.status || error?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound(`Leap Content Asset "${contentId}" Not Found.`)
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    if (!contentId || (!isLoading && !modules?.length)) {
        if (res) {
            res.statusCode = 404
        }
        return null
    }

    if (contentId && isLoading) {
        return (
            <Section>
                <Skeleton height="20px" />
            </Section>
        )
    }

    return (
        <LenisProvider>
            <Box display="flex" flexDirection="column" gap="10">
                <Heading
                    background={theme === 'dark' ? 'darkgray' : 'lightgray'}
                    color={theme === 'dark' ? 'lightgray' : 'darkgray'}
                    paddingBlock="10"
                    textAlign="center"
                >
                    {theme === 'dark' ? 'Dark theme' : 'Light theme'}
                </Heading>

                <RadioGroup onChange={setTheme} value={theme}>
                    <HStack justify="center" spacing={6}>
                        <Radio value="light">Light</Radio>
                        <Radio value="dark">Dark</Radio>
                    </HStack>
                </RadioGroup>

                <Box display="flex" justifyContent="center">
                    <Button variant="secondaryFilled" onClick={openInSidebar}>
                        <span>Open in sidebar</span>
                    </Button>
                </Box>

                <Chakra3Theme theme={theme}>
                    <CmsModules assetId={contentId} modules={modules} />
                </Chakra3Theme>
            </Box>
        </LenisProvider>
    )
}

LeapContent.shouldGetProps = () => false

export default LeapContent
